import {useFeatureFlags} from '@smart/feature-app';
import type {ShoppingCartDetails} from '@smart/website-shopping-cart-details';
import * as React from 'react';
import {FEATURE_FLAGS} from '../../types';
import {getDemoVehicleMode} from '../utils/client/get-demo-vehicle-mode';
import {getShowRoomVehicleMode} from '../utils/client/get-showroom-vehicle-mode';
import {useFeatureServices} from './use-feature-services';

export interface UseShoppingCartDetailsOptions {
  readonly enabled: boolean;
}

export function useShoppingCartDetails({
  enabled,
}: UseShoppingCartDetailsOptions): ShoppingCartDetails | undefined {
  const [shoppingCartDetails, setShoppingCartDetails] =
    React.useState<ShoppingCartDetails>();

  const {
    cartUpdateService,
    logger,
    loginService,
    pubSubService,
    adbContextService,
  } = useFeatureServices();
  const fetchAbortControllerRef = React.useRef<AbortController>();
  const {enabled: enableAnonymousFlow} = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_ANONYMOUS_FLOW,
  );

  React.useEffect(() => {
    if (
      (!enabled || loginService?.loginState.status !== `logged-in`) &&
      !enableAnonymousFlow
    ) {
      return;
    }

    const fetchShoppingCartDetails = () => {
      fetchAbortControllerRef.current?.abort();

      const fetchAbortController = (fetchAbortControllerRef.current =
        new AbortController());

      const isDemoVehicleSession = getDemoVehicleMode();
      const isShowRoomVehicleSession = getShowRoomVehicleMode();
      const isDemoVehicle =
        isDemoVehicleSession && adbContextService?.adbContext?.hasCustomer;
      const isShowRoomVehicle =
        isShowRoomVehicleSession && adbContextService?.adbContext?.hasCustomer;
      const searchParams = new URLSearchParams({
        isDemoVehicle: isDemoVehicle ? `true` : `false`,
        enableAnonymousFlow: String(enableAnonymousFlow),
        isShowRoomVehicle: isShowRoomVehicle ? `true` : `false`,
      });

      fetch(
        `${
          process.env.PRIVATE_BASE_URL
        }/shopping-cart-details/?${searchParams.toString()}`,
        {
          signal: fetchAbortController.signal,
        },
      )
        .then(async (response) => {
          if (response.ok) {
            setShoppingCartDetails(await response.json());
          } else if (response.status === 401) {
            logger.warn(`The user has an invalid session and is logged out.`);
            loginService?.logout();
          } else {
            logger.warn(`Unable to fetch shopping cart details.`);
          }
        })
        .catch((error) => {
          if (!fetchAbortController.signal.aborted) {
            logger.error(`Unable to fetch shopping cart details.`, error);
          }
        });
    };

    fetchShoppingCartDetails();

    const subscribeAbortController = new AbortController();

    cartUpdateService.subscribe(fetchShoppingCartDetails, {
      signal: subscribeAbortController.signal,
    });

    pubSubService.subscribe(`cart-update`, fetchShoppingCartDetails, {
      signal: subscribeAbortController.signal,
    });

    return () => {
      subscribeAbortController.abort();
      fetchAbortControllerRef.current?.abort();
    };
  }, [enabled]);

  return shoppingCartDetails;
}
